import classnames from 'classnames/bind';

import { Link } from 'components/link/Link';
import { ButtonIconList, Svg } from 'components/svg/Svg';

import s from './Button.module.scss';
import { HoverMagic } from 'components/hover-magic/HoverMagic';

const c = classnames.bind(s);

type ButtonDefaults = {
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  transition?: boolean;
  icon?: ButtonIconList;
  iconRight?: boolean;
  style?: 'primary' | 'secondary' | 'tertiary' | 'transparent' | 'simple';
};

type ButtonLinkProps = {
  to?: string;
  onClick?: never;
  transition?: boolean;
} & ButtonDefaults &
  React.AnchorHTMLAttributes<HTMLAnchorElement>;

type ButtonButtonProps = {
  to?: never;
  onClick?: () => void;
  transition?: never;
} & ButtonDefaults &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

// either a link or a button but dont mix!
export type ButtonProps = ButtonButtonProps | ButtonLinkProps;

export const Button = ({
  to,
  children,
  className,
  disabled,
  transition,
  icon,
  iconRight = true,
  style,
  ...props
}: ButtonProps) => {
  const classNames = c(className, s.button, {
    disabled,
    iconRight,
    iconLeft: !iconRight,
    [s.transparent]: style === 'transparent',
    [s.simple]: style === 'simple',
  });

  const Icon = () =>
    icon ? (
      <span className={s.button__iconWrap}>
        <Svg className={s.button__icon} id={icon} />
      </span>
    ) : null;

  if (to) {
    switch (style) {
      case 'transparent':
        return (
          <Link
            to={to}
            transition={transition}
            className={classNames}
            {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          >
            <HoverMagic className={s.alignment}>
              <span>{children}</span>
              <Icon />
            </HoverMagic>
          </Link>
        );
      case 'simple':
        return (
          <Link
            to={to}
            transition={transition}
            className={classNames}
            {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          >
            <div className={s.alignment}>
              <span>{children}</span>
              <Icon />
            </div>
          </Link>
        );
      default:
        return (
          <Link
            to={to}
            transition={transition}
            className={classNames}
            {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          >
            <Icon />
            <span>{children}</span>
          </Link>
        );
    }
  }

  return (
    <button
      disabled={disabled}
      aria-disabled={disabled}
      className={classNames}
      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      <Icon />
      <span>{children}</span>
    </button>
  );
};
